import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  computed,
  input,
  viewChild,
  output,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ReactiveFormsModule, FormControl, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyListModule as MatListModule } from '@angular/material/legacy-list';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatetimepickerModule } from '@zerops/zef/datetimepicker';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom
} from 'rxjs';
import sortBy from 'lodash-es/sortBy';
import formatISO from 'date-fns/formatISO';
import startOfMinute from 'date-fns/startOfMinute';
import parseISO from 'date-fns/parseISO';
import {
  RxVirtualFor,
  RxVirtualScrollViewportComponent,
  DynamicSizeVirtualScrollStrategy,
  RxVirtualScrollElementDirective
} from '@rx-angular/template/experimental/virtual-scrolling';

import { SatPopoverModule } from '@zerops/zef/popover';
import { ButtonProgressModule } from '@zerops/zef/progress';
import { ZefScrollbarComponent } from '@zerops/zef/scrollbar';
import { HashMap, ZefReactiveComponent } from '@zerops/zef/core';
import { SatPopover, SatPopoverAnchor } from '@zerops/zef/popover';
import { ZefFormFieldModule, ZefFormsModule } from '@zerops/zef/forms';
import { ZefFormModule } from '@zerops/zef/forms/modules';
import { ZefSnackService } from '@zerops/zef/snack';
import { VirtualScrollRefs } from '@zerops/zef/scrollbar';
import { ActionListItemModule } from '@zerops/zui/action-list-item';
import { LocalSearchFieldModule } from '@zerops/zui/local-search-field';
import { ZuiSeparatorComponent } from '@zerops/zui/separator';
import { ZuiTipCardComponent } from '@zerops/zui/tip-card';
import { ZuiMoreButtonModule } from '@zerops/zui/more-button';
import { ZuiThreeDotsModule } from '@zerops/zui/three-dots';
import { LogData } from '@zerops/models/project';
import { ServiceStack, ServiceStackTypes } from '@zerops/models/service-stack';
import { PersistencySettingsApi, RuntimeLogSettingNames } from '@zerops/zerops/core';
import { ProjectBaseApi } from '@zerops/zerops/core/project-base';
import { Container } from '@zerops/zerops/core/container-base';
import { FormatOptions, SEVERITIES, SEVERITIES_MAP, TimestampsOptions, TrLogOptions, TrLogParams } from './trlog.model';
import { TrLogComponentStore } from './trlog.store';
import { addTrLogParamsToUrl, deepCompare, getTrLogEndpoint, periodSettings, toStateApiParams } from './trlog.utils';
import { TrLogPeriods } from './trlog.constant';

@Component({
  selector: 'z-trlog',
  standalone: true,
  templateUrl: './trlog.feature.html',
  styleUrls: [ './trlog.feature.scss' ],
  imports: [
    CommonModule,
    RouterLink,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatListModule,
    MatInputModule,
    MatButtonToggleModule,
    MatDatepickerModule,
    MatDatetimepickerModule,
    RxVirtualFor,
    RxVirtualScrollElementDirective,
    RxVirtualScrollViewportComponent,
    DynamicSizeVirtualScrollStrategy,
    ZefFormModule,
    ZefFormFieldModule,
    ZefFormsModule,
    ZefScrollbarComponent,
    ButtonProgressModule,
    SatPopoverModule,
    ZuiSeparatorComponent,
    ZuiTipCardComponent,
    ZuiThreeDotsModule,
    ZuiMoreButtonModule,
    ActionListItemModule,
    LocalSearchFieldModule
  ],
  providers: [ TrLogComponentStore ]
})
export class TrLogFeature extends ZefReactiveComponent implements AfterViewInit {
  // # Data
  // -- sync
  formatOptions = FormatOptions;
  severities = SEVERITIES;
  severitiesMap = SEVERITIES_MAP;
  rangeControl = new FormGroup({
    from: new FormControl(undefined),
    till: new FormControl(undefined)
  });
  customRangeMode = false;
  zeropsTagName = 'zerops@zerops';
  activeCopyPasteOnlyBaseContentMode = this.#getCopyPasteOnlyBaseContentMode(RuntimeLogSettingNames.COPY_PASTE_ONLY_BASE_CONTENT);

  // -- async
  scrollIndexChange$ = new Subject<number>();
  options$ = new BehaviorSubject<any>(undefined);
  params$ = new BehaviorSubject<TrLogParams>(undefined);
  itemsRendered$ = new Subject<any[]>();
  onSearch$ = new Subject<string | null>();
  onOpenTabLogs$ = new Subject<FormatOptions>();

  items$ = this._trLogStore.select((s) => s.data.items);
  logTags$ = this._trLogStore.select((s) => s.info.tags).pipe(
    filter((d) => !!d),
    map((d) => sortBy(d, [
      (str) => !str.startsWith('zerops@zerops'),
      (str) => !str.startsWith('zbuilder@'),
      (str) => !str.startsWith('zerops@'),
      (str) => !str.includes('zerops@'),
      (str) => str.toLowerCase()
    ]))
  );
  tagParamsArr$ = this.params$.pipe(
    map((d) => d.tags?.split(','))
  );
  tagsParamsInfo$ = this.tagParamsArr$.pipe(
    map((tags) => {
      if (tags?.length > 2) {
        return `${tags.slice(0, 2).join(', ')} +${tags?.length - 2}`;
      } else {
        return tags?.join(', ');
      }
    })
  );
  tagParamsActiveMap$ = this.tagParamsArr$.pipe(
    map((tags) => Object.fromEntries(tags?.map(itm => [itm, true]) || []))
  );

  showLoadMore$ = this._trLogStore.select((s) => !!s.data?.hasMore);
  itemsLength$ = this.items$.pipe(map((d) => d?.length || 0), distinctUntilChanged());
  delayedShow$ = this.itemsLength$.pipe(
    delay(50),
    map((d) => !!d)
  );
  stateOptions$ = this._trLogStore.select((s) => s?.options);
  isLive$ = this.stateOptions$.pipe(map((d) => d?.live));
  isFollowing$ = this.stateOptions$.pipe(map((d) => d?.liveFollow));
  isLiveCancellable$ = this.stateOptions$.pipe(map((d) => d?.liveCancellable));
  isLoading$ = this._trLogStore.select((d) => d?.loading);
  isLoadMoreLoading$ = this._trLogStore.select((d) => d?.loadMoreLoading);
  viewportWidth: number;
  containerMap: HashMap<Container> = {};
  periods = {
    absolute: [
      {
        key: TrLogPeriods.Today,
        name: 'Today',
        value: periodSettings.today()
      },
      {
        key: TrLogPeriods.Yesterday,
        name: 'Yesterday',
        value: periodSettings.yesterday()
      },
      {
        key: TrLogPeriods.ThisWeek,
        name: 'This Week',
        value: periodSettings.thisWeek()
      },
      {
        key: TrLogPeriods.LastWeek,
        name: 'Last Week',
        value: periodSettings.lastWeek()
      }
    ],
    relative: [
      {
        key: TrLogPeriods.Last15Minutes,
        name: 'Last 15 Minutes',
        value: periodSettings.last15Minutes()
      },
      {
        key: TrLogPeriods.Last60Minutes,
        name: 'Last 60 Minutes',
        value: periodSettings.last60Minutes()
      },
      {
        key: TrLogPeriods.Last7Hours,
        name: 'Last 7 Hours',
        value: periodSettings.last7Hours()
      },
      {
        key: TrLogPeriods.Last24Hours,
        name: 'Last 24 Hours',
        value: periodSettings.last24Hours()
      },
      {
        key: TrLogPeriods.Last7Days,
        name: 'Last 7 Days',
        value: periodSettings.last7Days()
      }
    ]
  };
  periodsMap = {
    relative: Object.fromEntries(this.periods.relative.map(itm => [itm.key, itm])),
    absolute: Object.fromEntries(this.periods.absolute.map(itm => [itm.key, itm]))
  };
  serviceStackTypes = ServiceStackTypes;

  private _allowedRanges = [ 1000, 5000, 10000 ];
  private _rowSize = 22;
  private _itemPadding = 32;
  private _letterWidth = 7.26;
  private _containers: Container[];
  private _params: TrLogParams;
  private _rangeKeys = [ 'from', 'till' ];

  // Inputs
  showFilters = input<boolean>(false);
  showSearchInfoBox = input<boolean>(false);
  showHostname = input<boolean>(false);
  serviceStack = input<ServiceStack>();
  options = input<TrLogOptions>();

  @Input()
  set params(v: TrLogParams) {
    const parsedLimit = parseInt(v.limit as string, 10);
    const p = {
      ...v,
      limit: this._allowedRanges.includes(parsedLimit) ? parsedLimit : 1000
    };
    this.params$.next(p);
    this._params = p;

    if (v.mode === 'range') {
      this.#setRangeValues();
      this.customRangeMode = true;
    }
  }

  get params() {
    return this._params;
  }

  @Input()
  set containers(v) {
    this._containers = v;
    this.containerMap = v?.reduce((obj: HashMap<Container>, itm) => {
      obj[itm.id] = itm;
      return obj;
    }, {});
  }
  get containers() {
    return this._containers;
  }

  scrollHeight = input<string>('500px');
  virtualScrollKey = input<string>('logRuntimeRef');

  // Output
  filterChange = output<any>();
  moreOptions = output<SatPopover>();
  openVictoria = output<void>();

  // Inputs (Victoria switch)
  showVictoriaSwitch = input<boolean>(false);
  // True while the VictoriaLogs iframe overlay is on screen — hides the native
  // log viewport visually. The underlying log subscription stays warm so
  // close→restore is instant; this only gates the render, not the pipeline.
  victoriaActive = input<boolean>(false);

  // View Child
  scrollViewportRef = viewChild<RxVirtualScrollViewportComponent>(RxVirtualScrollViewportComponent);
  viewportRef = viewChild<ElementRef<HTMLElement>>('viewportRef');
  timePopRef = viewChild<SatPopover>('timePopRef');
  timeRangePopAnchorRef = viewChild<SatPopoverAnchor>('timeRangePopAnchorRef');
  moreOptionsRef = viewChild<SatPopover>('moreOptions');

  // Signals
  services = input<ServiceStack[]>();
  servicesMap = computed(() => {
    return this.services()?.reduce((obj: HashMap<ServiceStack>, itm) => {
      obj[itm.id] = itm;
      return obj;
    }, {});
  });
  projectDetailLink = computed(() => {
    const projectId = this.serviceStack()?.projectId;
    return projectId ? `/project/${projectId}/logging` : undefined;
  });

  state = this.$connect({
    showLoadMore: this.showLoadMore$,
    options: this.options$,
    tagParamsArr: this.tagParamsArr$,
    params: this.params$,
    tagsParamsInfo: this.tagsParamsInfo$,
    logTags: this.logTags$,
    tagParamsActiveMap: this.tagParamsActiveMap$,
    isLive: this.isLive$,
    isFollowing: this.isFollowing$,
    itemsLength: this.itemsLength$,
    isLoading: this.isLoading$,
    isLiveCancellable: this.isLiveCancellable$,
    isLoadMoreLoading: this.isLoadMoreLoading$,
    delayedShow: this.delayedShow$,
    hasMainTag: this.logTags$.pipe(
      map((tags) => tags.includes(this.zeropsTagName))
    ),
    searchTerm: this.onSearch$
  });

  dynamicSize = (item: LogData) => {
    // 10 = spacing between date and text
    // 24 = severity
    const totalWidthRequiredBase = (
      ((this.activeCopyPasteOnlyBaseContentMode ? '' : item.timestamp) + item.content).length * this._letterWidth)
      + (this.activeCopyPasteOnlyBaseContentMode ? 0 : 10)
      + 24;
    const totalWidthRequired = this.showHostname()
      ? totalWidthRequiredBase + (item.hostname.length * this._letterWidth)
      : totalWidthRequiredBase;

    const rows = Math.ceil(totalWidthRequired / this.viewportWidth);

    return Math.max(rows, 1) * this._rowSize;
  };

  #getCopyPasteOnlyBaseContentMode(runtimeLogSettingName: RuntimeLogSettingNames): boolean {
    return this._persistencySettingsApi.getLogsModePersistency(runtimeLogSettingName);
  }

  constructor(
    private _api: ProjectBaseApi,
    private _trLogStore: TrLogComponentStore,
    private _viewportRuler: ViewportRuler,
    private _virtualScrollRefs: VirtualScrollRefs,
    private _persistencySettingsApi: PersistencySettingsApi,
    private _snack: ZefSnackService
  ) {
    super();

    effect(() => {
      const options = this.options();
      this.options$.next(options);
    }, { allowSignalWrites: true });

    /**
     * Open a new tab with the raw log content (including the option to show/hide timestamps).
     */
    this.onOpenTabLogs$.pipe(
      withLatestFrom(this.params$),
      filter(([ _, d ]) => !!d),
      map(([ format, params ]) => ({
        ...params,
        ...{
          format,
          showTimestamps: this.activeCopyPasteOnlyBaseContentMode ? TimestampsOptions.HIDE : TimestampsOptions.SHOW
        },
      })),
      switchMap((params) => this._api.logUrl$(params.projectId, params.instanceId).pipe(
        map((log) => {
          const apiParams = toStateApiParams(params);
          return { urlPlain: log.urlPlain, apiParams };
        })
      )),
      takeUntil(this.onDestroy$)
    ).subscribe({
      next: ({ urlPlain, apiParams }) => {
        const endpointUrl = addTrLogParamsToUrl(getTrLogEndpoint(urlPlain, 'HTTP'), apiParams);
        window.open(endpointUrl, '_blank');
      },
      error: (err) => {
        console.error(err);
        this._snack.warning$({ text: 'A problem with getting the logs URL has been detected.'}, { duration: 60000 });
      }
    });

    // sets options
    combineLatest([
      this.params$.pipe(debounceTime(50), distinctUntilChanged(deepCompare)),
      this.options$.pipe(distinctUntilChanged(deepCompare))
    ])
      .pipe(
        tap(([ params, options ]) => {
          this._trLogStore.setParamsAndOptions({
            params,
            options: (options || {})
          });
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe();

    // handles scroll down cases
    this.itemsRendered$.pipe(
      filter((d) => !!d),
      take(1),
      switchMap(() => this.stateOptions$.pipe(
        switchMap(({ live, liveFollow }) => this.itemsLength$.pipe(
          distinctUntilChanged(),
          filter((d) => !!d),
          tap((data) => {
            if (!data || !this.scrollViewportRef()) { return; }

            if (live && liveFollow) {
              this.scrollViewportRef().scrollToIndex(data - 1);
            }

            if (!live && this._trLogStore.lastPosition) {
              this.scrollViewportRef().scrollToIndex(this._trLogStore.lastPosition - 5);
            }

            if (!live && !this._trLogStore.lastPosition) {
              this.scrollViewportRef().scrollToIndex(data - 1);
            }
          })
        )),
        takeUntil(this.onDestroy$)
      ))
    ).subscribe();

    this.onSearch$
      .pipe(
        withLatestFrom(this.params$),
        map(([ content, params ]) => ({ content: content.trim(), params })),
        tap(({ content, params }) =>
          params.mode === 'tail'
          ? this.filterChange.emit({ content, mode: 'limit', limit: '10000' })
          : this.filterChange.emit({ content })
        ),
        takeUntilDestroyed(),
      )
      .subscribe();

  }

  ngAfterViewInit() {

    this._viewportRuler
      .change(200)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.#calculateViewportWidth();
      });

    // removes live when scrolling up
    const createWheelEvent$ = (scrollEl: HTMLElement) => {
      return fromEvent(scrollEl, 'wheel').pipe(
        debounceTime(10),
        filter((e: WheelEvent) => e.deltaY < 0),
        map((e: WheelEvent) => ({ type: 'WHEEL_SCROLL_UP', e }))
      );
    };

    const createArrowUpEvent$ = (scrollEl: HTMLElement) => {
      scrollEl.setAttribute('tabindex', '0');
      return fromEvent<KeyboardEvent>(scrollEl, 'keydown').pipe(
        filter((e) => e.key === 'ArrowUp'),
        map((e) => ({ type: 'ARROW_UP', e }))
      );
    };

    setTimeout(() => {
      this.moreOptions.emit(this.moreOptionsRef());
      this.#calculateViewportWidth();
    }, 0);

    setTimeout(() => {
      const scrollEl = this.scrollViewportRef()?.getScrollElement();
      const wheelEvent$ = createWheelEvent$(scrollEl);
      const arrowUpEvent$ = createArrowUpEvent$(scrollEl);

      merge(wheelEvent$, arrowUpEvent$).pipe(
        withLatestFrom(this.stateOptions$),
        filter(([ _, { live } ]) => live),
        tap(([ _, { liveCancellable }]) => {
          if (liveCancellable) {
            this._trLogStore.updateOptions({
              live: false
            });
          } else {
            this._trLogStore.updateOptions({
              liveFollow: false
            });
          }
        }),
        takeUntil(this.onDestroy$)
      ).subscribe();
    }, 100);

    setTimeout(() => {
      this.#initScrollBar();
    }, 50);

  }

  setLive(state = true) {
    this._trLogStore.setLiveState(state);
    this._trLogStore.updateOptions({
      liveFollow: state
    });
  }

  setFollow(liveFollow = true) {
    this._trLogStore.updateOptions({ liveFollow });
    this.#scrollToBottom();
  }

  toTop() {
    this._trLogStore.updateOptions({
      liveFollow: false
    });
    this.#scrollToTop();
  }

  toBottom() {
    this.#scrollToBottom();
  }

  loadMore() {
    this._trLogStore.loadMore('prepend');
  }

  trackById(_: number, item: any) {
    return item?.id;
  }

  onTimePopRefOpen(mode: string) {
    if (mode === 'range') {
      setTimeout(() => {
        this.timeRangePopAnchorRef().popover.open();
        this.customRangeMode = true;
      });
    }
  }

  onTimeRangePopOpen() {
    this.timeRangePopAnchorRef().popover.open();
    this.customRangeMode = true;

    this.#setRangeValues();
    this._rangeKeys.forEach((n) => {
      const ctrl = this.rangeControl.get(n);
      ctrl?.markAsPristine();
      ctrl?.markAsUntouched();
    })
  }

  applyCustomRange() {
    if (this.rangeControl.valid) {
      this.filterChange.emit({
        mode: 'range',
        from: formatISO(startOfMinute(this.rangeControl.value?.from)),
        till: formatISO(startOfMinute(this.rangeControl.value?.till)),
        limit: null,
        period: null
      });
      this.timePopRef().close();
      this.timeRangePopAnchorRef().popover.close();
    } else {
      this._rangeKeys.forEach((n) => {
        const ctrl = this.rangeControl.get(n);
        ctrl?.markAsDirty();
        ctrl?.markAllAsTouched();
      });
    }
  }

  toggleTag(tag: string, activeTags: string[]) {
    // without the current tag
    if (activeTags?.includes(tag)) {
      const newTags = activeTags.filter((itm) => itm !== tag).join(',');

      // no tags after removing -> return undefined to clear the param
      if (!newTags.length) { return undefined; }
      return newTags;
    }

    // add tags
    return [
      ...(activeTags || []),
      tag
    ].join(',')

  }

  clipboardCopy(event: ClipboardEvent) {

    let transformedContent = '';
    const selection = window.getSelection();
    if (selection.rangeCount) {

      /**
       * Clone the selected DOM fragment.
       */
      const range = selection.getRangeAt(0);
      const cloned = range.cloneContents();

      /**
       * #Walk through nodes and build content with explicit newlines.
       */
      const walker = document.createTreeWalker(cloned, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
      let values: string[] = [];
      while (walker.nextNode()) {

        let isDiv = false;
        const node = walker.currentNode;

        if (node.nodeType === Node.TEXT_NODE) {
          values.push(node.nodeValue);
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          if (element.tagName === "DIV") {
            isDiv = true;
          }
        }

        /**
         * Omit the immediate reaction to the first DIV instance because it is necessary to collect values from it.
         */
        if (isDiv && values.length > 0) {
          const isFullLog = values.length > 2; // The number represents the number of SPAN tags in the source tree.
          values.forEach((value, index, array) => {
            if (index === array.length - 1) {
              if (value[0] !== ' ' && (!this.activeCopyPasteOnlyBaseContentMode || isFullLog)) {
                // Guarantee that the last value is separated by a space if there is more values than one.
                transformedContent += ' ';
              }
              transformedContent += value;
              transformedContent += '\n';
            } else {
              transformedContent += value;
            }
          });
          values = [];
        }

      }

      /**
       * Process the values from the last DIV.
       */
      if (values?.length > 0) {
        const isFullLog = values.length > 2; // The number represents the number of SPAN tags in the source tree.
        values.forEach((value, index, array) => {
          if (index === array.length - 1) {
            if (value[0] !== ' ' && (!this.activeCopyPasteOnlyBaseContentMode || isFullLog)) {
              // Guarantee that the last value is separated by a space if there is more values than one.
              transformedContent += ' ';
            }
            transformedContent += value;
          } else {
            transformedContent += value;
          }
        });
      }

    }

    /**
     * Normalize potentially multiple newlines and trim.
     */
    transformedContent.replace(/\n{2,}/g, "\n").trim();

    /**
     * Update the clipboard by the transformed content.
     */
    event.clipboardData.setData("text/plain", transformedContent);
    event.preventDefault();
  }

  #calculateViewportWidth() {
    this.viewportWidth = this.viewportRef().nativeElement.clientWidth - this._itemPadding;
  }

  #scrollToBottom() {
    this.scrollViewportRef().scrollToIndex(1000000000000000);
  }

  #scrollToTop() {
    this.scrollViewportRef().scrollTo(0);
  }

  #setRangeValues() {
    if (this._params?.mode === 'range') {
      this.rangeControl.setValue({
        from: parseISO(this._params?.from),
        till: parseISO(this._params?.till)
      });
    }
  }

  #initScrollBar() {
    if (this.scrollViewportRef()) {
      this._virtualScrollRefs.setVirtualScrollRef(this.virtualScrollKey(), this.scrollViewportRef());
    }
  }

  setCopyPasteOnlyBaseContentMode(copyPasteOnlyBaseContentMode: boolean): void {
    this._persistencySettingsApi.putLogsModePersistency(RuntimeLogSettingNames.COPY_PASTE_ONLY_BASE_CONTENT, copyPasteOnlyBaseContentMode);
    this.activeCopyPasteOnlyBaseContentMode = copyPasteOnlyBaseContentMode;
  }

}
